import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Construction, Coffee, Check } from "lucide-react";
import { trackEvent } from "@/lib/api";

const STORE_URL = "https://www.empresscoachingandconsulting.com/theroadmap";
const CALENDLY_URL = "https://calendly.com/jocelyn-empresscoachingandconsulting/one-on-one-30-min";
const LOGO_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_map-finder-47/artifacts/bl59sojl_499_logo-01.webp";
const HERO_PHOTO_B =
  "https://customer-assets-jai6qajn.emergentagent.net/job_map-finder-47/artifacts/50z7csyn_9943cbe993696ffd6be282543968d210cc094b1991301a45f6fc6da876f74502.webp";

const FERN = "#6B8E6B";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

const takeaways = [
  "A clearer picture of what you actually want",
  "Milestones and next steps you can act on",
  "A better understanding of what tends to get in your way",
  "A plan for the support and accountability that can help you keep going",
];

const modules = [
  { icon: Compass, title: "The Road Map", body: "Define what you want, why it matters, and the milestones that can get you there." },
  { icon: Construction, title: "Roadblocks & Detours", body: "Spot the internal patterns that can stop you, and separate them from real-life circumstances that require a change of plan." },
  { icon: Coffee, title: "Pit Stops", body: "Identify the support and self-maintenance you need to keep going." },
];

const purpleBtn =
  "inline-flex items-center gap-2 rounded-md px-8 py-4 font-semibold text-white bg-[#64246A] hover:bg-[#4A1A50] transition-colors duration-300";

export default function RoadMap() {
  useEffect(() => {
    trackEvent("road_map_view", null);
  }, []);

  return (
    <div className="min-h-screen bg-[#EAE6E1] text-[#2A1B2E]">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <Link to="/" data-testid="roadmap-brand-home" className="block h-12 w-48 overflow-hidden">
          <img src={LOGO_URL} alt="Empress Coaching and Consulting" className="h-full w-full object-cover object-center mix-blend-multiply" />
        </Link>
        <a
          href={STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="roadmap-nav-buy-btn"
          className="text-sm font-semibold uppercase tracking-[0.15em] text-white bg-[#64246A] hover:bg-[#4A1A50] rounded-md px-5 py-2.5 transition-colors duration-300"
        >
          Start - $75
        </a>
      </nav>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-20 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <div>
          <motion.p variants={fade} initial="hidden" animate="show" custom={0}
            className="text-sm uppercase tracking-[0.25em] font-semibold mb-6" style={{ color: FERN }}>
            Self-paced starter · $75
          </motion.p>
          <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
            The Road Map
          </motion.h1>
          <motion.h3 variants={fade} initial="hidden" animate="show" custom={2}
            className="mt-3 font-serif text-2xl sm:text-3xl text-[#2A1B2E]/80">
            Get clear on what you want. Build a plan you can actually use.
          </motion.h3>
          <motion.p variants={fade} initial="hidden" animate="show" custom={3}
            className="mt-6 text-lg text-[#2A1B2E]/75 max-w-md leading-relaxed">
            You know something could be different, but you may not know exactly what
            needs to change or where to start.
          </motion.p>
          <motion.p variants={fade} initial="hidden" animate="show" custom={4}
            className="mt-4 text-lg text-[#2A1B2E]/75 max-w-md leading-relaxed">
            The Road Map helps you get clear on what you actually want, identify what
            could get in the way, and turn it into concrete next steps.
          </motion.p>
          <motion.div variants={fade} initial="hidden" animate="show" custom={5} className="mt-9 flex flex-col items-start gap-4">
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="roadmap-hero-buy-btn" className={purpleBtn}>
              Start The Road Map - $75 <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/quiz" data-testid="roadmap-hero-quiz-link" className="text-sm font-semibold text-[#64246A] underline underline-offset-4 hover:opacity-80 transition-opacity">
              Not sure yet? Take the 2-minute quiz →
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-lg overflow-hidden shadow-[0_20px_60px_rgba(100,36,106,0.28)]"
        >
          <img src={HERO_PHOTO_B} alt="Jocelyn" className="w-full h-[420px] md:h-[520px] object-cover object-top" />
        </motion.div>
      </section>

      {/* What you'll walk away with */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">What you'll walk away with</h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-5">
            {takeaways.map((t, i) => (
              <li key={i} className="flex gap-3 text-lg text-[#2A1B2E]/80 leading-relaxed" data-testid={`roadmap-takeaway-${i}`}>
                <Check className="w-5 h-5 shrink-0 mt-1" style={{ color: FERN }} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What's inside */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <p className="text-sm uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: FERN }}>
          What's inside
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {modules.map((m, i) => (
            <motion.div
              key={m.title}
              variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-lg p-8 border border-[#2A1B2E]/10 bg-white"
              data-testid={`roadmap-module-${i}`}
            >
              <m.icon className="w-9 h-9 mb-6" strokeWidth={1.5} style={{ color: FERN }} />
              <h3 className="font-serif text-2xl mb-3">{m.title}</h3>
              <p className="text-[#2A1B2E]/75 leading-relaxed">{m.body}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-10 italic text-[#2A1B2E]/70">
          Three guided video lessons plus a fillable workbook. Go at your own pace. Come back anytime.
        </p>
      </section>

      {/* Meet Jocelyn (no photo) */}
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: FERN }}>
            Meet Jocelyn
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Hi, I'm Jocelyn.</h2>
          <p className="mt-6 text-lg text-[#2A1B2E]/75 leading-relaxed">
            I'm a Certified Professional Coach with years of coaching experience and
            more than 15 years leading teams, businesses, operations, and
            organizational change.
          </p>
          <p className="mt-4 text-lg text-[#2A1B2E]/75 leading-relaxed">
            I've learned that most people don't need someone else telling them what
            they should do. They often need space to see what they want more clearly,
            notice what may be getting in the way, and decide what comes next.
          </p>
          <p className="mt-4 text-lg text-[#2A1B2E]/75 leading-relaxed">
            That's what I designed The Road Map to help you do.
          </p>
        </div>
      </section>

      {/* Quiz section */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
          Not sure if The Road Map is where you should start?
        </h2>
        <p className="mt-6 text-lg text-[#2A1B2E]/75 leading-relaxed">
          Take the 2-minute quiz for a personalized look at what may be keeping you
          stuck and the next step I'd recommend.
        </p>
        <div className="mt-8 flex justify-center">
          <Link to="/quiz" data-testid="roadmap-quiz-btn" className={purpleBtn}>
            Take the 2-minute quiz <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Final purchase */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 pb-24">
        <div className="rounded-lg border border-[#2A1B2E]/10 bg-white px-8 md:px-16 py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Ready to get clearer?</h2>
          <p className="mt-6">
            <span className="font-serif text-5xl" style={{ color: FERN }}>$75</span>
            <span className="block mt-2 text-[#2A1B2E]/70">One-time payment.</span>
          </p>
          <p className="mt-4 text-[#2A1B2E]/75">
            Includes three guided video lessons and a fillable workbook. Yours to keep.
          </p>
          <div className="mt-8 flex flex-col items-center gap-5">
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="roadmap-invest-buy-btn" className={purpleBtn}>
              Start The Road Map - $75 <ArrowRight className="w-4 h-4" />
            </a>
            <div>
              <p className="text-[#2A1B2E]/70">Not sure you want to work through this on your own?</p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="roadmap-call-link"
                className="mt-1 inline-block text-sm font-semibold text-[#64246A] underline underline-offset-4 hover:opacity-80 transition-opacity"
              >
                Book a free 30-minute discovery call
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A1B2E]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#2A1B2E]/60">
          <span className="font-serif text-base text-[#2A1B2E]">Empress Coaching &amp; Consulting</span>
          <span>Lead from within. Create what matters.</span>
        </div>
      </footer>
    </div>
  );
}
